import { useEffect, useMemo, useState } from 'react';
import { MessagesSquare, Megaphone, ArrowLeft, ArrowUpRight, UserRound, ChevronDown } from 'lucide-react';
import { SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import SafeImage from '../../../components/SafeImage';
import SupportSocialChannels, { SupportSocialIcon } from './SupportSocialChannels';
import liveChatApi from '../../../services/liveChatApi';
import useSupportSwipeBack from './useSupportSwipeBack';
import {
  FacebookGlyph, DiscordGlyph, TelegramGlyph,
  InstagramGlyph, YoutubeGlyph, TikTokGlyph, WhatsAppGlyph,
} from './Glyphs';

/* -------------------- CHANNEL METADATA -------------------- */
const CHANNEL_META = {
  instagram: { label: 'Instagram', gradient: 'linear-gradient(135deg, #833AB4, #E1306C 50%, #FCB045 100%)', Glyph: InstagramGlyph },
  youtube:   { label: 'YouTube',   gradient: '#FF0000', Glyph: YoutubeGlyph },
  tiktok:    { label: 'TikTok',    gradient: '#000000', Glyph: TikTokGlyph,  cta: 'Open' },
  facebook:  { label: 'Facebook',  gradient: '#1877F2', Glyph: FacebookGlyph },
  whatsapp:  { label: 'WhatsApp',  gradient: '#25D366', Glyph: WhatsAppGlyph },
  discord:   { label: 'Discord',   gradient: '#5865F2', Glyph: DiscordGlyph,  cta: 'Join' },
  telegram:  { label: 'Telegram',  gradient: '#26A5E4', Glyph: TelegramGlyph },
  support_email: { label: 'Email', gradient: 'linear-gradient(135deg, #22FF88, #00A86B)', Glyph: null },
};

const SUPPORT_ACCENT_DIM = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 14%, transparent)';
const SUPPORT_ACCENT_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 48%, transparent)';
const SUPPORT_ACCENT_GLOW = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 34%, transparent)';

function firstUrl(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/https?:\/\/[^\s]+/i);
  return match ? match[0].replace(/[),.;]+$/, '') : '';
}

function normalizeChannelHref(key, value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const extracted = firstUrl(raw);
  if (extracted) return extracted;
  if (key === 'support_email') return raw.includes('@') ? `mailto:${raw.replace(/^mailto:/i, '')}` : '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (key === 'telegram') return `https://t.me/${raw.replace(/^@/, '').replace(/^t\.me\//i, '')}`;
  if (key === 'whatsapp') {
    if (/^(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com|whatsapp\.com)\//i.test(raw)) return `https://${raw}`;
    const digits = raw.replace(/[^\d+]/g, '').replace(/^\+/, '');
    return digits ? `https://wa.me/${digits}` : '';
  }
  return `https://${raw}`;
}

const agentUnreadKey = (id, name) => {
  const cleanId = String(id || '').trim();
  if (cleanId) return `id:${cleanId}`;
  const cleanName = String(name || '').trim().toLowerCase();
  return cleanName ? `name:${cleanName}` : 'support';
};

/* -------------------- SUPPORT MENU (default export) -------------------- */
export default function SupportMenu({ branding, chatUnread = 0, agentUnread = [], onLiveChat, onPickAgent, onCommunity, onOpenChannel, onManagePreferred, onClose }) {
  const rawSupport = branding?.support || {};
  const supportVisibility = branding?.support_visibility || {};
  const supportIcons = branding?.support_icons || {};
  const brandLogo = branding?.logo_url || branding?.intro_logo_url || '';
  const socials = {
    instagram: rawSupport.instagram || '',
    youtube: rawSupport.youtube || '',
    tiktok: rawSupport.tiktok || '',
    facebook: rawSupport.facebook || rawSupport.facebook_url || '',
    whatsapp: rawSupport.whatsapp || rawSupport.whatsapp_url || '',
    discord: rawSupport.discord || rawSupport.discord_url || '',
    telegram: rawSupport.telegram || rawSupport.telegram_url || '',
    support_email: rawSupport.support_email || rawSupport.email || '',
  };
  const [pickerOpen, setPickerOpen] = useState(false);
  const swipeBackRef = useSupportSwipeBack(onClose, pickerOpen);
  const [communityUnread, setCommunityUnread] = useState(0);
  const chatAgents = useMemo(() => {
    const raw = Array.isArray(branding?.chat_agents) ? branding.chat_agents : [];
    return raw
      .filter(agent => agent && agent.enabled !== false && String(agent.name || '').trim())
      .map((agent, index) => ({
        id: agent.id || `agent_${index}`,
        name: String(agent.name || '').trim(),
        title: agent.title || 'Support Agent',
        avatar_url: agent.avatar_url || '',
      }));
  }, [branding]);
  const visibleAgents = chatAgents.length ? chatAgents : [{ id: 'support-team', name: 'Support Team', title: 'Live Chat Agent', avatar_url: '' }];
  const unreadByAgent = useMemo(() => {
    const map = new Map();
    (Array.isArray(agentUnread) ? agentUnread : []).forEach(item => {
      const key = agentUnreadKey(item.agent_id, item.agent_name);
      map.set(key, {
        count: Number(item.unread_count || 0),
        lastMessage: item.last_message || '',
        lastMessageAt: item.last_message_at || '',
        avatar_url: item.avatar_url || '',
      });
    });
    return map;
  }, [agentUnread]);
  const unreadOnlyAgents = useMemo(() => {
    const visibleKeys = new Set(
      visibleAgents
        .map(agent => agentUnreadKey(agent.id, agent.name))
        .filter(Boolean)
    );
    return (Array.isArray(agentUnread) ? agentUnread : [])
      .filter(item => Number(item?.unread_count || 0) > 0)
      .map((item, index) => ({
        id: String(item.agent_id || '').trim() || `unread-agent-${index}`,
        name: String(item.agent_name || 'Support Team').trim(),
        title: item.agent_title || 'Support Agent',
        avatar_url: item.avatar_url || '',
      }))
      .filter(agent => !visibleKeys.has(agentUnreadKey(agent.id, agent.name)));
  }, [agentUnread, visibleAgents]);
  const agentRows = useMemo(() => {
    return [...visibleAgents, ...unreadOnlyAgents]
      .map(agent => {
        const unread = unreadByAgent.get(agentUnreadKey(agent.id, agent.name)) || unreadByAgent.get(agentUnreadKey(null, agent.name)) || { count: 0, lastMessage: '', lastMessageAt: '' };
        return {
          ...agent,
          avatar_url: unread.avatar_url || agent.avatar_url,
          unread,
        };
      })
      .sort((a, b) => {
        const unreadDiff = (b.unread?.count || 0) - (a.unread?.count || 0);
        if (unreadDiff) return unreadDiff;
        const aTime = Date.parse(a.unread?.lastMessageAt || '') || 0;
        const bTime = Date.parse(b.unread?.lastMessageAt || '') || 0;
        return bTime - aTime;
      });
  }, [visibleAgents, unreadOnlyAgents, unreadByAgent]);

  useEffect(() => {
    let active = true;
    const refreshCommunityUnread = async () => {
      try {
        const { data } = await liveChatApi.get('/api/chat/community/summary');
        if (active) setCommunityUnread(Math.max(0, Number(data?.unread_count || 0)));
      } catch {
        if (active) setCommunityUnread(0);
      }
    };
    refreshCommunityUnread();
    const timer = window.setInterval(refreshCommunityUnread, 30000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  const openChannel = (key) => {
    if (onOpenChannel) {
      onOpenChannel(key);
      return;
    }
    const url = socials[key];
    if (!url) return;
    const href = normalizeChannelHref(key, url);
    if (!href) return;
    window.open(href, '_blank', 'noopener,noreferrer');
    onClose?.();
  };

  const pickAgent = (agent) => {
    if (onPickAgent) onPickAgent(agent);
    else onLiveChat?.();
  };

  return (
    <div ref={swipeBackRef} className="support-menu-shell flex flex-col h-full" data-testid="support-swipe-surface">
      <SheetHeader className="support-menu-header">
        <button type="button" onClick={onClose} className="support-menu-back" aria-label="Back">
          <ArrowLeft size={20} aria-hidden="true" />
        </button>
        <div className="flex items-start gap-3">
          <div
            className="relative h-10 w-10 shrink-0 overflow-visible rounded-xl"
            style={{ filter: `drop-shadow(0 0 14px ${SUPPORT_ACCENT_GLOW})` }}
            aria-hidden
          >
            <div className="h-10 w-10 overflow-hidden rounded-xl bg-black/45">
              {brandLogo ? (
                <SafeImage
                  src={brandLogo}
                  alt=""
                  className="h-full w-full object-cover"
                  data-testid="support-brand-logo"
                  loading="lazy"
                  fallback={<MessagesSquare className="m-2 h-6 w-6 text-white/75" />}
                />
              ) : (
                <MessagesSquare className="m-2 h-6 w-6 text-white/75" />
              )}
            </div>
          </div>
          <div className="min-w-0 pt-0.5 text-left">
            <SheetTitle className="support-menu-title">Supports and Community</SheetTitle>
            <p className="support-menu-subtitle">Pick a channel to reach our team</p>
          </div>
        </div>
      </SheetHeader>

      <div className="p-4 space-y-3 overflow-y-auto min-h-0 flex-1" data-testid="support-menu">
        <div className="flex items-center gap-3 px-1 pt-1" data-testid="support-agents-heading">
          <span
            className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-2xl border bg-black/45"
            style={{ borderColor: SUPPORT_ACCENT_BORDER, boxShadow: `0 0 14px ${SUPPORT_ACCENT_GLOW}` }}
            aria-hidden
          >
            {brandLogo ? (
              <SafeImage
                src={brandLogo}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                fallback={<UserRound className="h-6 w-6 text-white/75" />}
              />
            ) : (
              <UserRound className="h-6 w-6 text-white/75" />
            )}
          </span>
          <div className="min-w-0 text-left">
            <p className="display text-base font-black tracking-wide text-white">OUR AGENTS</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.68)' }}>
              {chatUnread > 0 ? `${chatUnread} unread live chat message${chatUnread === 1 ? '' : 's'}` : 'Choose who you want to text'}
            </p>
          </div>
        </div>
        <div className="space-y-2 p-2" data-testid="support-agent-list">
          {agentRows.map(agent => (
            <button
              key={agent.id}
              type="button"
              onClick={() => pickAgent(agent)}
              className="w-full flex items-center gap-3 rounded-2xl p-3 text-left transition active:scale-[0.99] hover:bg-white/5"
              style={{ border: 0, background: 'rgba(255,255,255,0.03)' }}
              data-testid={`support-agent-${agent.id}`}
            >
              <span className="relative h-12 w-12 shrink-0 overflow-visible">
                <span className="grid h-12 w-12 place-items-center overflow-hidden rounded-full" style={{ background: SUPPORT_ACCENT_DIM, border: `1px solid ${SUPPORT_ACCENT_BORDER}`, boxShadow: `0 0 14px ${SUPPORT_ACCENT_GLOW}` }}>
                  {agent.avatar_url ? (
                    <SafeImage src={agent.avatar_url} alt="" className="h-full w-full object-cover" loading="lazy" fallback={<UserRound className="h-6 w-6 text-white" />} />
                  ) : (
                    <UserRound className="h-6 w-6 text-white" />
                  )}
                </span>
                <span
                  className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 bg-[#20F56B]"
                  style={{ borderColor: 'var(--vl-bg, #000000)', boxShadow: '0 0 10px rgba(32,245,107,0.9)' }}
                  aria-label={`${agent.name} online`}
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-extrabold text-white">{agent.name}</span>
                <span className="block truncate text-xs text-white/65">{agent.unread?.lastMessage || agent.title || 'Support Agent'}</span>
              </span>
              {(agent.unread?.count || 0) > 0 && (
                <span
                  className="min-w-[24px] h-6 px-1.5 rounded-full text-[11px] font-black text-white flex items-center justify-center"
                  style={{ background: '#FF3D71', border: '2px solid #fff', boxShadow: '0 0 12px rgba(255,61,113,0.72)' }}
                  data-testid={`support-agent-${agent.id}-badge`}
                >
                  {agent.unread.count > 9 ? '9+' : agent.unread.count}
                </span>
              )}
              <ChevronDown className="-rotate-90 h-5 w-5 text-white/55" />
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => onCommunity?.()}
          className="w-full flex items-center gap-3 rounded-2xl p-3 text-left transition active:scale-[0.99] hover:brightness-110"
          style={{ background: 'linear-gradient(135deg, rgba(42,171,238,0.20), rgba(118,102,255,0.16))', border: 0 }}
          data-testid="support-community"
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#2aabee]/20 text-[#69c9f4]" style={{ border: '1px solid rgba(91,190,245,0.44)' }}><Megaphone className="h-6 w-6" /></span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-base font-extrabold text-white">COMMUNITY</span>
            <span className="block truncate text-xs text-white/65">Join official updates, rewards, and announcements</span>
          </span>
          {communityUnread > 0 && (
            <span className="grid min-w-6 h-6 shrink-0 place-items-center rounded-full bg-[#FF3D71] px-1.5 text-[11px] font-black text-white" style={{ border: '2px solid #fff', boxShadow: '0 0 12px rgba(255,61,113,0.72)' }} data-testid="support-community-unread">
              {communityUnread > 99 ? '99+' : communityUnread}
            </span>
          )}
          <ChevronDown className="-rotate-90 h-5 w-5 text-white/55" />
        </button>


        <button
          type="button"
          onClick={() => {
            if (onManagePreferred) onManagePreferred();
            else setPickerOpen(true);
          }}
          className="support-social-launcher"
          data-testid="support-add-preferred"
        >
          <span className="support-social-launcher-title">Open social media support</span>
          <span className="support-social-launcher-bottom">
            <span className="support-social-launcher-icons" aria-hidden="true">
              {['facebook', 'instagram', 'whatsapp', 'discord', 'tiktok', 'telegram'].map(channel => (
                <SupportSocialIcon key={channel} channel={channel} meta={CHANNEL_META[channel]} />
              ))}
            </span>
            <span className="support-social-launcher-action">Open <ArrowUpRight size={15} aria-hidden="true" /></span>
          </span>
        </button>
      </div>

      <SupportSocialChannels
        open={pickerOpen}
        socials={Object.fromEntries(Object.entries(socials).filter(([key, url]) => url && supportVisibility[key] !== false))}
        icons={supportIcons}
        metadata={CHANNEL_META}
        onOpenChannel={openChannel}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}
